/**
 * The microphone choice as a hook: which inputs this machine has, which one
 * Automatic resolves to, which one the user picked, and how to change it.
 *
 * The capture counterpart of `use-dictation-engine-choice.ts`. The device list
 * is native state the webview cannot observe, so it is read when the row
 * mounts and again whenever the window regains focus - which is when a user
 * comes back from plugging something in. There is no polling.
 */
import { useCallback, useEffect, useState } from "react";
import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";
import { useDictationSettingsStore } from "@/stores/dictation-settings-store";

type VoiceInputDeviceKind =
  | "built_in"
  | "wired"
  | "bluetooth"
  | "virtual"
  | "other";

type VoiceInputDevice = {
  id: string;
  name: string;
  kind: VoiceInputDeviceKind;
  is_system_default: boolean;
};

export type VoiceInputDevices = {
  devices: VoiceInputDevice[];
  /** What Automatic resolves to right now; null when there is no input. */
  automatic_id: string | null;
};

/** What a pick reports: never the device's name or id. */
export type VoiceInputChoice = {
  selection: "automatic" | "specific";
  kind: VoiceInputDeviceKind | null;
};

const NO_DEVICES: VoiceInputDevice[] = [];

export function useVoiceInputDevices(options?: {
  onChoose?: (choice: VoiceInputChoice) => void;
}) {
  const selectedId = useDictationSettingsStore((s) => s.inputDeviceId);
  const selectedName = useDictationSettingsStore((s) => s.inputDeviceName);
  const setInputDevice = useDictationSettingsStore((s) => s.setInputDevice);
  const onChoose = options?.onChoose;

  // Null until the first list arrives, and again whenever a list fails: the
  // row has nothing truthful to show in either case, so it stays hidden.
  const [listing, setListing] = useState<VoiceInputDevices | null>(null);
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    let latest = 0;
    const load = () => {
      // Only the newest request may answer: a slow list from before a refocus
      // must not overwrite the one read after it.
      const request = ++latest;
      const settle = (next: VoiceInputDevices | null) => {
        if (!cancelled && request === latest) setListing(next);
      };
      invoke<VoiceInputDevices>("list_voice_input_devices").then(settle, () =>
        settle(null),
      );
    };
    load();
    window.addEventListener("focus", load);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", load);
    };
  }, []);

  const devices = listing?.devices ?? NO_DEVICES;
  const automaticDevice =
    devices.find((device) => device.id === listing?.automatic_id) ?? null;

  const choose = useCallback(
    (id: string | null) => {
      if (id === null) {
        setInputDevice(null);
        onChoose?.({
          selection: "automatic",
          kind: automaticDevice?.kind ?? null,
        });
        return;
      }
      // An id that is not in the list can only be the saved device, shown as
      // not connected: picking it again changes nothing.
      const device = devices.find((candidate) => candidate.id === id);
      if (!device) return;
      setInputDevice({ id: device.id, name: device.name });
      onChoose?.({ selection: "specific", kind: device.kind });
    },
    [automaticDevice, devices, onChoose, setInputDevice],
  );

  return {
    /** Whether there is a list to choose from: desktop, and the list loaded. */
    available: listing !== null,
    devices,
    /** The device Automatic uses right now, or null when there is no input. */
    automaticDevice,
    /** The persisted choice, or null while Automatic. */
    selectedId,
    /** The chosen device's name as saved, for when it is not in the list. */
    selectedName,
    /** Whether the chosen device is plugged in right now. */
    selectedConnected: devices.some((device) => device.id === selectedId),
    choose,
  };
}
